'use client'

import ConfirmModal from '@/components/confirm-modal'
import MedicationListEditor, {
    emptyMedication,
    MedicationItem,
} from '@/components/medical/medication-list-editor'
import SaveAsTemplateModal from '@/components/medical/save-as-template-modal'
import TemplatePicker from '@/components/medical/template-picker'
import { TemplateCategory } from '@/lib/api/templates/template-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'
import {
    createPrescription,
    CreatePrescriptionData,
    CreatePrescriptionItemData,
    deletePrescription,
    getPrescriptionsByClient,
    Prescription,
    updatePrescription,
} from '@/lib/api/prescriptions/prescription-api'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdClose,
    MdDelete,
    MdDraw,
    MdEdit,
    MdEventBusy,
    MdMedication,
    MdPerson,
    MdReceipt,
    MdVerified,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

interface PrescriptionFormData {
    professionalId: string
    validUntil: string
    content: string
    observations: string
}

const defaultFormData: PrescriptionFormData = {
    professionalId: '',
    validUntil: '',
    content: '',
    observations: '',
}

/**
 * Receituário do paciente.
 *
 * ⚠️ Esta tela NÃO navega para lugar nenhum, e é regra do prontuário, não estilo:
 * o médico não sai do prontuário para nada. Ela roda em DUAS superfícies — a rota
 * `/person/clients/:id/prontuario/receituario` e o modal aberto pelo menu
 * "Prontuário" das telas de atendimento —, e naquele modal qualquer `navigate()`
 * para fora do prontuário abre uma ABA NOVA (ver `prontuario-menu.tsx`),
 * arrancando o médico do atendimento em andamento.
 *
 * Por isso criar, editar, ver e assinar acontecem em modal aqui dentro, como em
 * `AtestadosPage` — atestado e receita são o mesmo tipo de documento. O overlay do
 * prontuário usa z-index 900, ABAIXO do 999 do `.modal` do DaisyUI, justamente
 * para estes modais abrirem por cima dele.
 *
 * A listagem geral (`/medical/prescriptions`) continua existindo como rota, mas
 * saiu do menu: receita não existe sem paciente.
 */
export default function ReceituarioPage() {
    const { client, clientId } = useProntuarioData()
    const { user, isProfessional } = useAuth()

    const [prescriptions, setPrescriptions] = useState<Prescription[]>([])
    const [loading, setLoading] = useState(true)

    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formData, setFormData] =
        useState<PrescriptionFormData>(defaultFormData)
    const [medications, setMedications] = useState<MedicationItem[]>([])
    const [saving, setSaving] = useState(false)

    const [viewing, setViewing] = useState<Prescription | null>(null)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<number | null>(null)

    // Oferecer "salvar como modelo" para quem acabou de APLICAR um seria propor
    // guardar o que já está guardado.
    const [usedTemplate, setUsedTemplate] = useState(false)
    const [pendingTemplate, setPendingTemplate] = useState<{
        content: string
        notes?: string
        medications: MedicationItem[]
        professionalId: number | null
    } | null>(null)

    const [showSignModal, setShowSignModal] = useState(false)
    const [prescriptionToSign, setPrescriptionToSign] =
        useState<Prescription | null>(null)

    useEffect(() => {
        if (clientId) {
            loadPrescriptions()
        }
    }, [clientId])

    const loadPrescriptions = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getPrescriptionsByClient(
                parseInt(clientId, 10)
            )
            if (response.success) {
                setPrescriptions(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar o receituário')
            }
        } catch (error) {
            toast.error('Erro ao carregar o receituário')
        } finally {
            setLoading(false)
        }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setFormData(defaultFormData)
        setMedications([{ ...emptyMedication }])
        setUsedTemplate(false)
        setModalOpen(true)
    }

    const openEditModal = (prescription: Prescription) => {
        setEditingId(prescription.id)
        setFormData({
            professionalId: prescription.professionalId
                ? String(prescription.professionalId)
                : '',
            validUntil: prescription.validUntil
                ? prescription.validUntil.split('T')[0]
                : '',
            content: prescription.content || '',
            observations: prescription.observations || '',
        })
        setMedications(
            (prescription.items ?? []).map(item => ({
                medication: item.medication || '',
                dosage: item.dosage || '',
                frequency: item.frequency || '',
                duration: item.duration || '',
                instructions: item.instructions || '',
                quantity: item.quantity ? String(item.quantity) : '',
            }))
        )
        setViewing(null)
        setUsedTemplate(false)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingId(null)
        setFormData(defaultFormData)
        setMedications([])
    }

    const updateFormField = (
        field: keyof PrescriptionFormData,
        value: string
    ) => {
        setFormData(previous => ({ ...previous, [field]: value }))
    }

    /**
     * O modelo SUBSTITUI o texto e SOMA os medicamentos.
     *
     * O texto tem um lugar só — dois modelos concatenados não formam receita
     * nenhuma. Os medicamentos, sim: o médico parte de um modelo e acrescenta o que
     * aquele paciente precisa, e apagar a escolha dele seria destrutivo. A linha em
     * branco que o formulário abre não conta como escolha.
     */
    const handleApplyTemplate = (applied: {
        content: string | null
        notes: string | null
        medications: {
            medication: string
            dosage?: string
            frequency?: string
            duration?: string
            instructions?: string
            quantity?: number
        }[]
    }) => {
        setUsedTemplate(true)
        setFormData(previous => ({
            ...previous,
            content: applied.content || previous.content,
            observations: applied.notes || previous.observations,
        }))
        setMedications(previous => [
            ...previous.filter(item => item.medication.trim()),
            ...applied.medications.map(item => ({
                medication: item.medication,
                dosage: item.dosage ?? '',
                frequency: item.frequency ?? '',
                duration: item.duration ?? '',
                instructions: item.instructions ?? '',
                quantity: item.quantity ? String(item.quantity) : '',
            })),
        ])
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!clientId) return

        if (!formData.content.trim()) {
            toast.error('O conteúdo da receita é obrigatório')
            return
        }
        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione o profissional que assina a receita')
            return
        }

        // Linha em branco é o normal ao abrir o formulário; só o que tem nome vai.
        const items: CreatePrescriptionItemData[] = medications
            .filter(item => item.medication.trim())
            .map(item => ({
                medication: item.medication.trim(),
                dosage: item.dosage.trim() || undefined,
                frequency: item.frequency.trim() || undefined,
                duration: item.duration.trim() || undefined,
                instructions: item.instructions.trim() || undefined,
                quantity: item.quantity
                    ? parseInt(item.quantity, 10)
                    : undefined,
            }))

        setSaving(true)
        try {
            if (editingId) {
                const response = await updatePrescription(editingId, {
                    content: formData.content.trim(),
                    observations: formData.observations.trim() || undefined,
                    validUntil: formData.validUntil || undefined,
                    items,
                })
                if (response.success) {
                    toast.success('Receita atualizada com sucesso!')
                    await loadPrescriptions()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao atualizar a receita')
                }
            } else {
                const createData: CreatePrescriptionData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    professionalId: isProfessional
                        ? user!.professionalId!
                        : parseInt(formData.professionalId, 10),
                    content: formData.content.trim(),
                    observations: formData.observations.trim() || undefined,
                    validUntil: formData.validUntil || undefined,
                    items,
                }

                const response = await createPrescription(createData)
                if (response.success) {
                    toast.success('Receita criada com sucesso!')
                    await loadPrescriptions()

                    // O momento é este: o médico só descobre que uma receita vale
                    // virar modelo depois de montá-la pela terceira vez.
                    if (!usedTemplate) {
                        setPendingTemplate({
                            content: createData.content,
                            notes: createData.observations,
                            medications: medications.filter(item =>
                                item.medication.trim()
                            ),
                            professionalId: createData.professionalId,
                        })
                    }

                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao criar a receita')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar a receita')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = (id: number) => {
        setDeleteTarget(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return

        try {
            const response = await deletePrescription(deleteTarget)
            if (response.success) {
                toast.success('Receita excluída com sucesso!')
                setViewing(null)
                await loadPrescriptions()
            } else {
                toast.error(response.error || 'Erro ao excluir a receita')
            }
        } catch (error) {
            toast.error('Erro ao excluir a receita')
        } finally {
            setShowDeleteModal(false)
            setDeleteTarget(null)
        }
    }

    /** Resumo dos medicamentos para a linha da lista, sem abrir o detalhe. */
    const getMedicationSummary = (prescription: Prescription): string => {
        const items = prescription.items ?? []
        if (items.length === 0) {
            return prescription.content?.trim() || 'Sem itens registrados'
        }

        const names = items.map(item => item.medication).filter(Boolean)
        if (names.length <= 3) return names.join(', ')
        return `${names.slice(0, 3).join(', ')} e mais ${names.length - 3}`
    }

    const isExpired = (prescription: Prescription): boolean => {
        if (!prescription.validUntil) return false
        return new Date(prescription.validUntil) < new Date()
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando receituário...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Cabeçalho */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Receituário
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Receitas emitidas para o paciente
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-1"
                        onClick={openCreateModal}
                    >
                        <MdAdd className="text-base" />
                        Nova receita
                    </button>
                    <span className="badge badge-lg badge-ghost">
                        {prescriptions.length}{' '}
                        {prescriptions.length === 1 ? 'receita' : 'receitas'}
                    </span>
                </div>
            </div>

            {/* Lista */}
            {prescriptions.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdReceipt className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhuma receita registrada
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {prescriptions.map(prescription => (
                        <div
                            key={prescription.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => setViewing(prescription)}
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdReceipt className="text-xl text-secondary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-base-content truncate">
                                                    Receita #{prescription.id}
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    {format(
                                                        new Date(
                                                            prescription.prescriptionDate ||
                                                                prescription.createdAt
                                                        ),
                                                        "dd/MM/yyyy 'às' HH:mm",
                                                        { locale: ptBR }
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {prescription.signedAt ? (
                                                <span className="badge badge-success gap-1 text-white">
                                                    <MdVerified className="text-sm" />
                                                    Assinada
                                                </span>
                                            ) : (
                                                <span className="badge badge-ghost">
                                                    Sem assinatura
                                                </span>
                                            )}
                                            {isExpired(prescription) && (
                                                <span className="badge badge-warning gap-1">
                                                    <MdEventBusy className="text-sm" />
                                                    Vencida
                                                </span>
                                            )}
                                            {prescription.items?.length ? (
                                                <span className="badge badge-ghost gap-1">
                                                    <MdMedication className="text-sm" />
                                                    {prescription.items.length}{' '}
                                                    {prescription.items
                                                        .length === 1
                                                        ? 'item'
                                                        : 'itens'}
                                                </span>
                                            ) : null}
                                        </div>

                                        <p className="text-sm text-base-content/70 line-clamp-2 mb-3">
                                            {getMedicationSummary(prescription)}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-4 text-xs text-base-content/50">
                                            {prescription.professional && (
                                                <div className="flex items-center gap-1">
                                                    <MdPerson className="text-sm" />
                                                    <span>
                                                        {
                                                            prescription
                                                                .professional
                                                                .name
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                            {prescription.validUntil && (
                                                <div className="flex items-center gap-1">
                                                    <MdEventBusy className="text-sm" />
                                                    <span>
                                                        Válida até{' '}
                                                        {format(
                                                            new Date(
                                                                prescription.validUntil
                                                            ),
                                                            'dd/MM/yyyy',
                                                            { locale: ptBR }
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        className="ml-2 flex items-center gap-1"
                                        onClick={event => event.stopPropagation()}
                                    >
                                        {!prescription.signedAt && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openEditModal(prescription)
                                                }
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Editar receita"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPrescriptionToSign(
                                                    prescription
                                                )
                                                setShowSignModal(true)
                                            }}
                                            className="btn btn-ghost btn-sm btn-square text-secondary"
                                            title="Assinar e gerar PDF"
                                        >
                                            <MdDraw className="text-lg" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleDelete(prescription.id)
                                            }
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                            title="Excluir receita"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Detalhe — em modal para não tirar o médico do prontuário. */}
            {viewing && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                Receita #{viewing.id}
                            </h3>
                            <button
                                onClick={() => setViewing(null)}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                                {viewing.signedAt ? (
                                    <span className="badge badge-success gap-1 text-white">
                                        <MdVerified className="text-sm" />
                                        Assinada em{' '}
                                        {format(
                                            new Date(viewing.signedAt),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )}
                                    </span>
                                ) : (
                                    <span className="badge badge-ghost">
                                        Sem assinatura
                                    </span>
                                )}
                                {isExpired(viewing) && (
                                    <span className="badge badge-warning gap-1">
                                        <MdEventBusy className="text-sm" />
                                        Vencida
                                    </span>
                                )}
                            </div>

                            {viewing.professional && (
                                <div>
                                    <p className="text-xs text-base-content/50 uppercase tracking-wide mb-1">
                                        Profissional
                                    </p>
                                    <p className="text-sm">
                                        {viewing.professional.name}
                                        {viewing.professional
                                            .registrationNumber &&
                                            ` — ${viewing.professional.councilName ?? ''} ${viewing.professional.registrationNumber}`}
                                    </p>
                                </div>
                            )}

                            <div>
                                <p className="text-xs text-base-content/50 uppercase tracking-wide mb-1">
                                    Conteúdo
                                </p>
                                <p className="text-sm whitespace-pre-wrap">
                                    {viewing.content}
                                </p>
                            </div>

                            {viewing.items?.length ? (
                                <div>
                                    <p className="text-xs text-base-content/50 uppercase tracking-wide mb-2">
                                        Medicamentos
                                    </p>
                                    <div className="space-y-2">
                                        {viewing.items.map(item => (
                                            <div
                                                key={item.id}
                                                className="bg-base-200/50 rounded-lg p-3 border border-base-300/50"
                                            >
                                                <p className="font-medium text-sm">
                                                    {item.medication}
                                                    {item.dosage &&
                                                        ` — ${item.dosage}`}
                                                </p>
                                                <p className="text-xs text-base-content/60">
                                                    {[
                                                        item.frequency,
                                                        item.duration,
                                                        item.quantity
                                                            ? `Quantidade: ${item.quantity}`
                                                            : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </p>
                                                {item.instructions && (
                                                    <p className="text-xs text-base-content/60 mt-1">
                                                        {item.instructions}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {viewing.observations && (
                                <div>
                                    <p className="text-xs text-base-content/50 uppercase tracking-wide mb-1">
                                        Observações
                                    </p>
                                    <p className="text-sm whitespace-pre-wrap">
                                        {viewing.observations}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            {!viewing.signedAt && (
                                <button
                                    type="button"
                                    className="btn btn-ghost gap-2"
                                    onClick={() => openEditModal(viewing)}
                                >
                                    <MdEdit className="text-lg" />
                                    Editar
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-secondary gap-2"
                                onClick={() => {
                                    setPrescriptionToSign(viewing)
                                    setShowSignModal(true)
                                }}
                            >
                                <MdDraw className="text-lg" />
                                Assinar e gerar PDF
                            </button>
                            <button
                                type="button"
                                className="btn"
                                onClick={() => setViewing(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Criar / editar */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingId ? 'Editar Receita' : 'Nova Receita'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Só na criação: aplicar modelo sobre uma receita já
                                emitida sobrescreveria o que o médico revisou. */}
                            {!editingId && clientId && (
                                <TemplatePicker
                                    category={TemplateCategory.PRESCRIPTION}
                                    clientId={parseInt(clientId, 10)}
                                    professionalId={
                                        isProfessional
                                            ? user?.professionalId
                                            : formData.professionalId
                                              ? parseInt(
                                                    formData.professionalId,
                                                    10
                                                )
                                              : null
                                    }
                                    onApply={handleApplyTemplate}
                                />
                            )}

                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Profissional{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={
                                            formData.professionalId
                                                ? Number(
                                                      formData.professionalId
                                                  )
                                                : null
                                        }
                                        onChange={id =>
                                            updateFormField(
                                                'professionalId',
                                                id ? String(id) : ''
                                            )
                                        }
                                        label=""
                                        placeholder="Selecione o profissional..."
                                        disabled={!!editingId}
                                    />
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Validade
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.validUntil}
                                    onChange={event =>
                                        updateFormField(
                                            'validUntil',
                                            event.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Conteúdo{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Conteúdo da receita..."
                                    value={formData.content}
                                    onChange={event =>
                                        updateFormField(
                                            'content',
                                            event.target.value
                                        )
                                    }
                                    rows={4}
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Observações
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Observações adicionais..."
                                    value={formData.observations}
                                    onChange={event =>
                                        updateFormField(
                                            'observations',
                                            event.target.value
                                        )
                                    }
                                    rows={3}
                                />
                            </div>

                            <div className="border-t border-base-300/50 pt-4">
                                <MedicationListEditor
                                    value={medications}
                                    onChange={setMedications}
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={closeModal}
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-secondary"
                                    disabled={saving}
                                >
                                    {saving
                                        ? 'Salvando...'
                                        : editingId
                                          ? 'Salvar alterações'
                                          : 'Criar receita'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setDeleteTarget(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir receita"
                message="Tem certeza que deseja excluir esta receita?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />

            {pendingTemplate && (
                <SaveAsTemplateModal
                    isOpen
                    onClose={() => setPendingTemplate(null)}
                    category={TemplateCategory.PRESCRIPTION}
                    content={pendingTemplate.content}
                    notes={pendingTemplate.notes}
                    medications={pendingTemplate.medications.map(item => ({
                        medication: item.medication.trim(),
                        dosage: item.dosage.trim() || undefined,
                        frequency: item.frequency.trim() || undefined,
                        duration: item.duration.trim() || undefined,
                        instructions: item.instructions.trim() || undefined,
                        quantity: item.quantity
                            ? parseInt(item.quantity, 10)
                            : undefined,
                    }))}
                    professionalId={pendingTemplate.professionalId}
                    onSaved={() => setPendingTemplate(null)}
                />
            )}

            {prescriptionToSign && (
                <SignDocumentModal
                    isOpen={showSignModal}
                    onClose={() => {
                        setShowSignModal(false)
                        setPrescriptionToSign(null)
                    }}
                    documentType="PRESCRIPTION"
                    documentId={prescriptionToSign.id}
                    documentTitle={`Receita #${prescriptionToSign.id} — ${client?.name ?? 'paciente'}`}
                    onSigned={() => loadPrescriptions()}
                />
            )}
        </div>
    )
}
